import classNames from 'classnames';
import { isNil, omitBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FiDownload, FiPlus } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { IPropsEbookSubmissionListItem } from '../../app/ebookSubmissions/components/EbookSubmissionListItem';
import EbookSubmissionFilterPresenter from '../../app/ebookSubmissions/presenters/EbookSubmissionFilterPresenter';
import EbookSubmissionListTablePresenter from '../../app/ebookSubmissions/presenters/EbookSubmissionListTablePresenter';
import ConfirmModal from '../../app/modals/ConfirmModal';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import { PintoEbookSubmissionStatus } from '../../core/graphql/types';
import useMutationCancelEbookSubmission from '../../hooks/useMutationCancelEbookSubmission';
import useQueryEbookSubmissions from '../../hooks/useQueryEbookSubmissions';
const PAGE_LIMIT = 10;
const TEMPLATE_URL_PATH = '../../a/template/Ebook_Submission_Template.xlsx';

const StatusOptions = [
  {
    label: 'All status',
    value: null,
  },
  {
    label: 'Pending',
    value: PintoEbookSubmissionStatus.Pending,
  },
  {
    label: 'Processing',
    value: PintoEbookSubmissionStatus.Processing,
  },
  {
    label: 'Completed',
    value: PintoEbookSubmissionStatus.Completed,
  },
  {
    label: 'Cancelled',
    value: PintoEbookSubmissionStatus.Cancelled,
  },
];

const EbookSubmissionPage = () => {
  const { ebookSubmissions, fetchEbookSubmissions, isLoadingEbookSubmissions } = useQueryEbookSubmissions();
  const { cancelEbookSubmission } = useMutationCancelEbookSubmission();

  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<PintoEbookSubmissionStatus | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // modal
  const [showConfirmCancel, setShowConfirmCancel] = useState<boolean>(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | undefined>(undefined);

  const handleChangePage = useCallback(
    (page: number) => {
      const offset = (page - 1) * PAGE_LIMIT;
      setCurrentPage(page);
      const filter = omitBy({ anyStatuses: status ? [status] : undefined, q: searchText, offset }, isNil);
      fetchEbookSubmissions(filter);
    },
    [fetchEbookSubmissions, searchText, status],
  );

  useEffect(() => {
    const filter = omitBy({ anyStatuses: status ? [status] : undefined, q: searchText }, isNil);
    fetchEbookSubmissions(filter);
  }, [fetchEbookSubmissions, searchText, status]);

  const handleSetSearchText = useCallback((text?: string) => {
    setCurrentPage(1);
    setSearchText(text);
  }, []);

  const handleSetStatus = useCallback((_status: PintoEbookSubmissionStatus | null) => {
    setCurrentPage(1);
    setStatus(_status);
  }, []);

  const handleOnCancel = useCallback((id: string) => {
    setSelectedSubmissionId(id);
    setShowConfirmCancel(true);
  }, []);

  const onConfirmCancel = useCallback(async () => {
    if (selectedSubmissionId) {
      const res = await cancelEbookSubmission(selectedSubmissionId);
      if (res.success) {
        ToastComponent({ label: 'Ebook submission cancelled', type: IToastType.SUCCESS });
        const filter = omitBy({ anyStatuses: status ? [status] : undefined, q: searchText }, isNil);
        fetchEbookSubmissions(filter);
      }
    }
    setShowConfirmCancel(false);
    setSelectedSubmissionId(undefined);
  }, [cancelEbookSubmission, fetchEbookSubmissions, searchText, selectedSubmissionId, status]);

  return (
    <div className="mx-auto space-y-24">
      {showConfirmCancel && (
        <ConfirmModal
          onClose={() => setShowConfirmCancel(false)}
          onConFirm={onConfirmCancel}
          title="Cancel submission"
          subTitle="Are you sure you want to cancel this ebook submission? This cannot be undone."
          confirmText="Cancel submission"
          cancelText="Keep it"
        />
      )}

      <div className="space-y-24 divider-y">
        {/* section: title */}
        <div className="flex justify-between py-5">
          <h1 className="font-dbh text-[28px]">Ebook Submissions</h1>

          <div className="space-x-16">
            <a href={TEMPLATE_URL_PATH} download="Ebook_Submission_Template.xlsx" className="space-x-8 btn btn-error btn-outline">
              <FiDownload className="font-bold text-24" />
              <span>Download Template</span>
            </a>

            <Link to="/ebookSubmissions/create">
              <button className="space-x-8 btn btn-error">
                <FiPlus className="font-bold text-24" />
                <span>Create Ebook Submission</span>
              </button>
            </Link>
          </div>
        </div>

        {/* filter and search */}
        <div className="space-y-8">
          <EbookSubmissionFilterPresenter onSetSearchText={handleSetSearchText} status={status} onSetStatus={handleSetStatus} statusOptions={StatusOptions} />
        </div>
      </div>

      {/* table content */}
      <div>
        {isLoadingEbookSubmissions && !ebookSubmissions && <div className="w-full btn btn-outline btn-disabled bg-systemGrays04LabelQuaternary border-none loading h-[100px]" />}

        {(!isLoadingEbookSubmissions || ebookSubmissions) && (
          <div className={classNames('transition-opacity', { 'opacity-50 pointer-events-none': isLoadingEbookSubmissions })}>
            {(ebookSubmissions?.edges?.length || 0) === 0 && (
              <div className="flex flex-col items-center justify-center w-full h-[60vh] space-y-8 text-colorsBrandWarmBlack02LabelSecondary">
                <span>No ebook submissions yet</span>
                <span>Create one to upload a new batch of ebooks.</span>
              </div>
            )}

            {(ebookSubmissions?.edges?.length || 0) !== 0 && (
              <EbookSubmissionListTablePresenter
                ebookSubmissionLists={ebookSubmissions?.edges.map(
                  ({ node }) =>
                    ({
                      _id: node._id,
                      fileName: node.fileName,
                      status: node.status,
                      itemCount: node.itemCount,
                      actorUsername: node.actor?.username,
                      adminNote: node.adminNote,
                      createdAt: node.createdAt,
                    } as IPropsEbookSubmissionListItem),
                )}
                currentPage={currentPage}
                totalCount={ebookSubmissions?.totalCount}
                onChangePage={handleChangePage}
                onCancel={handleOnCancel}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EbookSubmissionPage;
